/**
 * @typedef {import('./DeletesHttp').default} DeletesHttp
 * @typedef {import('./GetsHttp').default} GetsHttp
 * @typedef {import('./HttpMethods').default} HttpMethods
 * @typedef {import('./PatchesHttp').default} PatchesHttp
 * @typedef {import('./PostsHttp').default} PostsHttp
 * @typedef {import('./PutsHttp').default} PutsHttp
 * @typedef {import('node-fetch').default} fetch
 * @typedef {import('node-fetch').Headers} NodeHeaders
 * @typedef {import('node-fetch').Response} NodeResponse
 */

/**
 * @template Body
 * @typedef {import('./HttpResponse').default<Body>} HttpResponse
 */

/**
 * @abstract
 * @implements {DeletesHttp}
 * @implements {GetsHttp}
 * @implements {PatchesHttp}
 * @implements {PostsHttp}
 * @implements {PutsHttp}
 */
class HttpClient {
	/**
	 * @template ResponseBody
	 * @param {string} url
	 * @param {Headers|NodeHeaders=} headers
	 * @param {boolean=} includeCredentials
	 * @returns {Promise<HttpResponse<ResponseBody>>}
	 */
	delete(url, headers, includeCredentials) {
		return this.makeRequest(
			'DELETE',
			url,
			headers,
			undefined,
			includeCredentials
		);
	}

	/**
	 * @template ResponseBody
	 * @param {string} url
	 * @param {Headers|NodeHeaders=} headers
	 * @param {boolean=} includeCredentials
	 * @returns {Promise<HttpResponse<ResponseBody>>}
	 */
	get(url, headers, includeCredentials) {
		return this.makeRequest(
			'GET',
			url,
			headers,
			undefined,
			includeCredentials
		);
	}

	/**
	 * @template ResponseBody
	 * @param {string} url
	 * @param {Headers|NodeHeaders=} headers
	 * @param {object|string=} body
	 * @param {boolean=} includeCredentials
	 * @returns {Promise<HttpResponse<ResponseBody>>}
	 */
	patch(url, headers, body, includeCredentials) {
		return this.makeRequest(
			'PATCH',
			url,
			headers,
			body,
			includeCredentials
		);
	}

	/**
	 * @template ResponseBody
	 * @param {string} url
	 * @param {Headers|NodeHeaders=} headers
	 * @param {object|string=} body
	 * @param {boolean=} includeCredentials
	 * @returns {Promise<HttpResponse<ResponseBody>>}
	 */
	post(url, headers, body, includeCredentials) {
		return this.makeRequest('POST', url, headers, body, includeCredentials);
	}

	/**
	 * @template ResponseBody
	 * @param {string} url
	 * @param {Headers|NodeHeaders=} headers
	 * @param {object|string=} body
	 * @param {boolean=} includeCredentials
	 * @returns {Promise<HttpResponse<ResponseBody>>}
	 */
	put(url, headers, body, includeCredentials) {
		return this.makeRequest('PUT', url, headers, body, includeCredentials);
	}

	/**
	 * @param {any} body
	 * @param {?string} contentType
	 * @returns {string}
	 */
	parseRequestBody(body, contentType) {
		return contentType?.includes('application/json')
			? JSON.stringify(body)
			: body;
	}

	/**
	 * @template ResponseBody
	 * @param {Response|NodeResponse} response
	 * @param {?string} contentType
	 * @returns {Promise<HttpResponse<ResponseBody>>}
	 */
	async parseResponse(response, contentType) {
		return {
			body: await this.parseResponseBody(response, contentType),
			headers: response.headers,
			isOk: response.ok,
			statusCode: response.status,
		};
	}

	/**
	 * @template ResponseBody
	 * @abstract
	 * @param {HttpMethods} method
	 * @param {string} url
	 * @param {Headers|NodeHeaders=} headers
	 * @param {object|string=} body
	 * @param {boolean=} includeCredentials
	 * @returns {Promise<HttpResponse<ResponseBody>>}
	 */
	async makeRequest(method, url, headers, body, includeCredentials = false) {
		throw new Error(
			'HttpClient.makeRequest(method, url, headers, body, includeCredentials) is an abstract method and must be implemented by descendent class.'
		);
	}

	/**
	 * @template ResponseBody
	 * @param {Response} response
	 * @param {?string} contentType
	 * @returns {Promise<ResponseBody>}
	 */
	parseResponseBody(response, contentType) {
		if (contentType?.includes('application/json')) {
			return response.json()
		}

		if (contentType?.includes('text/')) {
			return response.text()
		}

		if (contentType?.includes('application/octet-stream')) {
			return response.blob()
		}

		return response.body;
	}
}

module.exports = HttpClient;
